'use client';

import { useState } from 'react';
import { MapPinIcon } from '@heroicons/react/20/solid';

import { Text, useTranslations } from '@northladder/i18n';
import { ICoordinate } from '@northladder/services';
import { TTextLocalized, TUIError } from '@northladder/utilities';

import { cn } from '../../../../../lib';

import { IBaseGoogleMapLocationOptions } from '../../types';

import { GoogleMapModal } from '../GoogleMapModal';

interface IGoogleMapLocationPickerProps extends IBaseGoogleMapLocationOptions {
  gpsError: TUIError;
  latitudeLabel?: string;
  longitudeLabel?: string;
}

interface IGPSTriggerProps {
  classNameLabel?: string;
  error: TUIError;
  label?: TTextLocalized;
  latitudeLabel: string;
  longitudeLabel: string;
  onClickTrigger: () => void;
  placeholder: TTextLocalized;
  selectedPlace: ICoordinate | null;
}

/**
 * -----------------------------------------------------------------------------
 * This triggers the the map on click, and displays the selected location
 * coordinates if available.
 */
function GPSTrigger({
  classNameLabel,
  error,
  label,
  latitudeLabel,
  longitudeLabel,
  onClickTrigger,
  placeholder,
  selectedPlace,
}: IGPSTriggerProps) {
  const { title, lat, lng } = selectedPlace || {};
  const latitude = lat?.toPrecision(8);
  const longitude = lng?.toPrecision(8);

  const gpsCordClass = title ? '' : ' ltr:pr-8 rtl:pl-8';

  return (
    <div className="w-full">
      {label ? (
        <label
          className={cn(
            'text-foreground mb-2 block text-sm font-medium',
            classNameLabel
          )}
          htmlFor="gps-map-picker"
        >
          <Text text={label} />
        </label>
      ) : null}

      <button
        className={cn(
          'bg-background text-foreground border-input hover:bg-accent',
          'relative inline-flex w-full flex-wrap justify-between rounded-md border',
          'px-3 py-2 text-sm shadow-sm transition-colors',
          'focus:ring-ring focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive focus:ring-destructive'
        )}
        onClick={onClickTrigger}
        type="button"
      >
        {selectedPlace ? (
          <div className="flex w-full flex-col gap-3">
            {title ? (
              <div className="border-border flex items-center justify-between border-b pb-2">
                <p className="text-foreground text-sm font-medium">{title}</p>
                <MapPinIcon className="text-muted-foreground h-5 w-5 shrink-0" />
              </div>
            ) : null}

            <div
              className={`
                flex grow flex-col justify-start gap-2
                ${gpsCordClass}
              `}
            >
              <div className="flex items-center justify-between gap-2">
                <dt
                  className="
                    text-muted-foreground w-16 text-xs font-medium
                    sm:w-auto
                  "
                >
                  {latitudeLabel}
                </dt>
                <dd
                  className="
                    bg-muted text-foreground border-border rounded-md
                    border px-3 py-1.5 font-mono text-xs
                  "
                >
                  {latitude}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt
                  className="
                    text-muted-foreground w-16 text-xs font-medium
                    sm:w-auto
                  "
                >
                  {longitudeLabel}
                </dt>
                <dd
                  className="
                    bg-muted text-foreground border-border rounded-md
                    border px-3 py-1.5 font-mono text-xs
                  "
                >
                  {longitude}
                </dd>
              </div>
            </div>

            {!title ? (
              <MapPinIcon
                className="
                  text-muted-foreground absolute top-1/2 h-6 w-6 shrink-0
                  -translate-y-1/2 ltr:right-2 rtl:left-2
                "
              />
            ) : null}
          </div>
        ) : (
          <>
            <div className="relative flex w-full items-center justify-start">
              <Text
                className="text-muted-foreground placeholder font-light"
                text={placeholder}
              />
            </div>
            <MapPinIcon
              className="
                text-muted-foreground absolute top-1/2 h-6 w-6 shrink-0
                -translate-y-1/2 ltr:right-2 rtl:left-2
              "
            />
          </>
        )}
      </button>

      {error?.message ? (
        <p className="text-destructive pt-2 text-xs">
          <Text text={error.message} />
        </p>
      ) : null}
    </div>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This provides a component that picks the map displays the current map location
 * that also dubs as a trigger to open the map.
 */
export function GoogleMapLocationPicker({
  classNameContainer,
  classNameLabel,
  initLocation,
  label,
  latitudeLabel,
  longitudeLabel,
  onUpdateLocation,
  placeholder = 'Pick the location on the map',
  regionKey,
  gpsError,
}: IGoogleMapLocationPickerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = useTranslations('DesignSystemComponents.gpsField');

  const handleModalOpen = () => {
    setIsModalOpen(() => true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={classNameContainer}>
      <GPSTrigger
        classNameLabel={classNameLabel}
        error={gpsError}
        label={label}
        latitudeLabel={latitudeLabel || t('latitude')}
        longitudeLabel={longitudeLabel || t('longitude')}
        onClickTrigger={handleModalOpen}
        placeholder={placeholder}
        selectedPlace={initLocation}
      />
      <GoogleMapModal
        initLocation={initLocation}
        isModalOpen={isModalOpen}
        onCloseModal={handleModalClose}
        onUpdateLocation={onUpdateLocation}
        regionKey={regionKey}
      />
    </div>
  );
}
